import React, { useState, useEffect, useContext } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";

import { AuthContext } from "../contexts/AuthContext";
import { ClientContext } from "../contexts/ClientContext";
import { ScreenContext } from "../contexts/ScreenContext";

import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Collapse,
  CardHeader,
  Avatar,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import DescriptionIcon from "@material-ui/icons/Description";
import CloudIcon from "@material-ui/icons/Cloud";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import BusinessIcon from "@material-ui/icons/Business";
import PersonIcon from "@material-ui/icons/Person";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f5f5",
    minHeight: "calc(100vh - 64px)",
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 1.5),
    },
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  pageTitle: {
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: "#666",
    fontSize: "0.95rem",
  },
  tabsContainer: {
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(3),
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  tab: {
    fontWeight: 600,
    fontSize: "0.95rem",
    textTransform: "none",
    minWidth: 160,
    [theme.breakpoints.down('sm')]: {
      minWidth: 120,
      fontSize: "0.85rem",
      padding: theme.spacing(1.5, 2),
    },
  },
  controlsCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5),
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  resultsCard: {
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  reportHeader: {
    padding: theme.spacing(3),
    backgroundColor: "#fafafa",
    borderBottom: "2px solid #e0e0e0",
  },
  reportTitle: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: "#1a1a1a",
    marginBottom: theme.spacing(0.5),
  },
  reportSubtitle: {
    color: "#666",
    fontSize: "0.9rem",
  },
  table: {
    "& .MuiTableCell-head": {
      backgroundColor: "#f5f5f5",
      fontWeight: 700,
      fontSize: "0.85rem",
      color: "#424242",
      borderBottom: "2px solid #e0e0e0",
      padding: theme.spacing(1.5, 2),
      [theme.breakpoints.down('sm')]: {
        fontSize: "0.75rem",
        padding: theme.spacing(1, 1),
      },
    },
    "& .MuiTableCell-body": {
      fontSize: "0.85rem",
      padding: theme.spacing(1.25, 2),
      borderBottom: "1px solid #f0f0f0",
      [theme.breakpoints.down('sm')]: {
        fontSize: "0.75rem",
        padding: theme.spacing(1, 1),
      },
    },
    "& .MuiTableRow-root:hover": {
      backgroundColor: "#fafafa",
    },
  },
  descriptionCell: {
    minWidth: 200,
    maxWidth: 400,
    [theme.breakpoints.down('sm')]: {
      minWidth: 150,
      maxWidth: 200,
      '& div': {
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        lineHeight: 1.4,
      },
    },
  },
  sectionHeader: {
    padding: theme.spacing(2),
    backgroundColor: "#f9f9f9",
    borderTop: "1px solid #e0e0e0",
    borderBottom: "1px solid #e0e0e0",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    color: "#424242",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  summaryCard: {
    padding: theme.spacing(3),
    backgroundColor: "#f9f9f9",
    borderTop: "2px solid #e0e0e0",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 0),
  },
  summaryLabel: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#424242",
  },
  summaryValue: {
    fontWeight: 700,
    fontSize: "1rem",
    color: "#1976d2",
  },
  totalRow: {
    borderTop: "2px solid #424242",
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
  },
  chipDirect: {
    backgroundColor: "#4caf50",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  chipIndirect: {
    backgroundColor: "#ff9800",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  doAssetsCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#e3f2fd",
    borderRadius: theme.spacing(1),
    border: "1px solid #90caf9",
  },
  doAssetRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 0),
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: "#999",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
  },
  clientCard: {
    marginBottom: theme.spacing(2),
    border: "2px solid #e0e0e0",
    borderRadius: theme.spacing(1),
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      borderColor: "#1976d2",
    },
  },
  clientCardHeader: {
    backgroundColor: "#f5f5f5",
    cursor: "pointer",
    padding: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#eeeeee",
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
  },
  clientSummary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    [theme.breakpoints.down('sm')]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
    },
  },
  clientInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
    },
  },
  clientAvatar: {
    backgroundColor: "#1976d2",
    width: theme.spacing(6),
    height: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(4.5),
      height: theme.spacing(4.5),
    },
  },
  clientStats: {
    display: "flex",
    gap: theme.spacing(3),
    alignItems: "center",
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
      width: "100%",
      justifyContent: "space-between",
    },
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down('sm')]: {
      alignItems: "flex-start",
    },
  },
  statLabel: {
    fontSize: "0.7rem",
    color: "#666",
    textTransform: "uppercase",
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: "0.65rem",
    },
  },
  statValue: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#1976d2",
    [theme.breakpoints.down('sm')]: {
      fontSize: "0.95rem",
    },
  },
  employeeCard: {
    marginBottom: theme.spacing(1.5),
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: theme.spacing(0.5),
    backgroundColor: "#fafafa",
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  employeeHeader: {
    padding: theme.spacing(1.5, 2),
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5, 1.5),
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(1),
    },
  },
  employeeInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  employeeAvatar: {
    backgroundColor: "#4caf50",
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: "0.9rem",
  },
  employeeStats: {
    display: "flex",
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexWrap: "wrap",
      gap: theme.spacing(1),
      width: "100%",
    },
  },
  miniStat: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#666",
    [theme.breakpoints.down('sm')]: {
      fontSize: "0.75rem",
    },
  },
}));

// Helper function to generate bi-weekly periods starting from Mondays
const generateBiWeeklyPeriods = (count = 6) => {
  const periods = [];
  let currentDate = moment();
  
  // Find the most recent Monday
  while (currentDate.day() !== 1) {
    currentDate.subtract(1, 'day');
  }
  
  // Generate periods going backwards
  for (let i = 0; i < count; i++) {
    const endDate = currentDate.clone().subtract(1, 'day');
    const startDate = endDate.clone().subtract(13, 'days'); // 14 days total (including both days)
    
    periods.push({
      label: `${startDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    });
    
    currentDate.subtract(14, 'days');
  }
  
  return periods;
};

const BillingReports = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, token } = useContext(AuthContext);
  const { clients, loadClients } = useContext(ClientContext);
  const { setSnackbarMessage } = useContext(ScreenContext);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/";

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Client Billing State
  const [selectedClient, setSelectedClient] = useState("");
  const [clientData, setClientData] = useState(null);
  
  // Employee Payroll State
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [employees, setEmployees] = useState([]);
  
  // By Date State
  const [byDateData, setByDateData] = useState(null);
  const [expandedClients, setExpandedClients] = useState({});
  const [expandedEmployees, setExpandedEmployees] = useState({});
  
  // Monthly DO Assets State
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));
  const [monthlyDOData, setMonthlyDOData] = useState(null);
  const [expandedDOClients, setExpandedDOClients] = useState({});
  
  // Date Range State
  const [datePreset, setDatePreset] = useState("custom");
  const [startDate, setStartDate] = useState(moment().subtract(13, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  
  // DO Projects for client assets
  const [doProjects, setDoProjects] = useState([]);

  const biWeeklyPeriods = generateBiWeeklyPeriods(6);

  useEffect(() => {
    if (user.type === 'super_admin') {
      loadClients();
      loadEmployees();
      loadDigitalOceanProjects();
    }
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await axios.post(
        `${API_URL}get-users`,
        { active: true },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setEmployees(response.data.users || []);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadDigitalOceanProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}digitalocean`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDoProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error loading DO projects:', error);
    }
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const period = biWeeklyPeriods.find(p => p.label === preset);
      if (period) {
        setStartDate(period.startDate);
        setEndDate(period.endDate);
      }
    }
  };

  const fetchClientBilling = async () => {
    if (!selectedClient || !startDate || !endDate) {
      setSnackbarMessage({
        message: "Please select a client and date range",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}billing/client`,
        {
          clientId: selectedClient,
          startDate,
          endDate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setClientData(response.data);
      setSnackbarMessage({
        message: "Client billing report generated successfully",
        severity: "success",
      });
    } catch (error) {
      console.error('Error fetching client billing:', error);
      setSnackbarMessage({
        message: error.response?.data?.error || "Error generating report",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeePayroll = async () => {
    if (!selectedEmployee || !startDate || !endDate) {
      setSnackbarMessage({
        message: "Please select an employee and date range",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}billing/employee`,
        {
          employee: selectedEmployee,
          startDate,
          endDate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEmployeeData(response.data);
      setSnackbarMessage({
        message: "Employee payroll report generated successfully",
        severity: "success",
      });
    } catch (error) {
      console.error('Error fetching employee payroll:', error);
      setSnackbarMessage({
        message: error.response?.data?.error || "Error generating report",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchByDateReport = async () => {
    if (!startDate || !endDate) {
      setSnackbarMessage({
        message: "Please select a date range",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}billing/by-date`,
        {
          startDate,
          endDate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setByDateData(response.data);
      setExpandedClients({});
      setExpandedEmployees({});
      setSnackbarMessage({
        message: "By date report generated successfully",
        severity: "success",
      });
    } catch (error) {
      console.error('Error fetching by date report:', error);
      setSnackbarMessage({
        message: error.response?.data?.error || "Error generating report",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleClientExpanded = (clientId) => {
    setExpandedClients(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  const toggleEmployeeExpanded = (clientId, employeeName) => {
    const key = `${clientId}-${employeeName}`;
    setExpandedEmployees(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleDOClientExpanded = (clientId) => {
    setExpandedDOClients(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  const fetchMonthlyDOAssets = async () => {
    if (!selectedMonth) {
      setSnackbarMessage({
        message: "Please select a month",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      // Get all clients with DO projects
      const clientsWithAssets = clients
        .map(client => {
          const assets = getClientDOAssets(client.ID);
          if (assets) {
            return {
              clientId: client.ID,
              clientName: client.Name,
              ...assets
            };
          }
          return null;
        })
        .filter(c => c !== null)
        .sort((a, b) => a.clientName.localeCompare(b.clientName));

      setMonthlyDOData({
        month: selectedMonth,
        clients: clientsWithAssets,
        totalCost: clientsWithAssets.reduce((sum, c) => sum + c.totalCost, 0)
      });
      
      setExpandedDOClients({});
      setSnackbarMessage({
        message: "Monthly DO assets report generated successfully",
        severity: "success",
      });
    } catch (error) {
      console.error('Error generating monthly DO assets report:', error);
      setSnackbarMessage({
        message: "Error generating report",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getClientDOAssets = (clientId) => {
    if (!clientId || doProjects.length === 0) return null;
    
    const clientIdStr = clientId.toString().trim();
    const matchingProjects = doProjects.filter(project => {
      const projectName = project.name.trim();
      if (projectName.includes('|')) {
        const parts = projectName.split('|').map(p => p.trim());
        const projectId = parts[0];
        return projectId === clientIdStr;
      }
      return false;
    });

    if (matchingProjects.length === 0) return null;

    const resources = {};
    let totalCost = 0;

    matchingProjects.forEach(project => {
      project.resources?.forEach(resource => {
        const type = resource.type;
        const cost = resource.details?.price_monthly || 0;
        
        if (!resources[type]) {
          resources[type] = { count: 0, cost: 0 };
        }
        resources[type].count++;
        resources[type].cost += cost;
        totalCost += cost;
      });
    });

    return { resources, totalCost };
  };

  // Excel Export Functions
  const exportClientBillingToExcel = () => {
    if (!clientData) return;

    const workbook = XLSX.utils.book_new();
    
    // Detailed hours data with all information
    const allHours = [];
    
    // Add Direct Hours
    clientData.directHours.forEach(row => {
      allHours.push({
        'Type': 'Direct (Billable)',
        'Date': moment(row.date).format('MMM D, YYYY'),
        'Employee': row.employee_name,
        'Start Time': moment(row.start_time, 'HH:mm:ss').format('h:mm A'),
        'End Time': moment(row.end_time, 'HH:mm:ss').format('h:mm A'),
        'Hours': parseFloat(row.hours).toFixed(2),
        'Description': row.description.replace(/<[^>]*>/g, ''),
      });
    });

    // Add Indirect Hours
    clientData.indirectHours.forEach(row => {
      allHours.push({
        'Type': 'Indirect (Non-Billable)',
        'Date': moment(row.date).format('MMM D, YYYY'),
        'Employee': row.employee_name,
        'Start Time': moment(row.start_time, 'HH:mm:ss').format('h:mm A'),
        'End Time': moment(row.end_time, 'HH:mm:ss').format('h:mm A'),
        'Hours': parseFloat(row.hours).toFixed(2),
        'Description': row.description.replace(/<[^>]*>/g, ''),
      });
    });

    // Add summary rows at the bottom
    allHours.push({});
    allHours.push({
      'Type': 'SUMMARY',
      'Date': '',
      'Employee': '',
      'Start Time': '',
      'End Time': '',
      'Hours': '',
      'Description': ''
    });
    allHours.push({
      'Type': 'Client:',
      'Date': clientData.clientName,
      'Employee': `ID: ${clientData.clientId}`,
      'Start Time': '',
      'End Time': '',
      'Hours': '',
      'Description': ''
    });
    allHours.push({
      'Type': 'Period:',
      'Date': moment(clientData.startDate).format('MMM D, YYYY'),
      'Employee': 'to',
      'Start Time': moment(clientData.endDate).format('MMM D, YYYY'),
      'End Time': '',
      'Hours': '',
      'Description': ''
    });
    allHours.push({});
    allHours.push({
      'Type': 'Direct (Billable) Hours:',
      'Date': '',
      'Employee': '',
      'Start Time': '',
      'End Time': '',
      'Hours': clientData.summary.totalDirectHours.toFixed(2),
      'Description': ''
    });
    allHours.push({
      'Type': 'Indirect (Non-Billable) Hours:',
      'Date': '',
      'Employee': '',
      'Start Time': '',
      'End Time': '',
      'Hours': clientData.summary.totalIndirectHours.toFixed(2),
      'Description': ''
    });
    allHours.push({
      'Type': 'TOTAL HOURS:',
      'Date': '',
      'Employee': '',
      'Start Time': '',
      'End Time': '',
      'Hours': clientData.summary.totalHours.toFixed(2),
      'Description': ''
    });

    // Add DO Assets if available
    const doAssets = getClientDOAssets(clientData.clientId);
    if (doAssets) {
      allHours.push({});
      allHours.push({
        'Type': 'DIGITAL OCEAN INFRASTRUCTURE',
        'Date': '',
        'Employee': '',
        'Start Time': '',
        'End Time': '',
        'Hours': '',
        'Description': ''
      });
      Object.entries(doAssets.resources).forEach(([type, data]) => {
        allHours.push({
          'Type': `${type.charAt(0).toUpperCase() + type.slice(1)}s:`,
          'Date': `${data.count} resources`,
          'Employee': '',
          'Start Time': '',
          'End Time': '',
          'Hours': '',
          'Description': `$${data.cost.toFixed(2)}/month`
        });
      });
      allHours.push({
        'Type': 'Total Monthly Infrastructure Cost:',
        'Date': '',
        'Employee': '',
        'Start Time': '',
        'End Time': '',
        'Hours': '',
        'Description': `$${doAssets.totalCost.toFixed(2)}/month`
      });
    }

    const sheet = XLSX.utils.json_to_sheet(allHours);
    XLSX.utils.book_append_sheet(workbook, sheet, 'Client Billing Report');

    XLSX.writeFile(workbook, `Client_Billing_${clientData.clientName}_${moment().format('YYYY-MM-DD')}.xlsx`);
    setSnackbarMessage({ message: "Excel file exported successfully", severity: "success" });
  };

  const exportEmployeePayrollToExcel = () => {
    if (!employeeData) return;

    const workbook = XLSX.utils.book_new();
    
    // Combine direct and indirect hours into detailed data
    const allHours = [];
    
    // Add direct hours
    employeeData.directHours.forEach(row => {
      allHours.push({
        'Date': moment(row.date).format('MMM D, YYYY'),
        'Type': 'Direct (Billable)',
        'Client ID': row.client_id,
        'Client Name': row.client_name || 'Unknown',
        'Start Time': moment(row.start_time, 'HH:mm:ss').format('h:mm A'),
        'End Time': moment(row.end_time, 'HH:mm:ss').format('h:mm A'),
        'Hours': parseFloat(row.hours).toFixed(2),
        'Description': row.description.replace(/<[^>]*>/g, ''),
      });
    });
    
    // Add indirect hours
    employeeData.indirectHours.forEach(row => {
      allHours.push({
        'Date': moment(row.date).format('MMM D, YYYY'),
        'Type': 'Indirect (Non-Billable)',
        'Client ID': row.client_id,
        'Client Name': row.client_name || 'Unknown',
        'Start Time': moment(row.start_time, 'HH:mm:ss').format('h:mm A'),
        'End Time': moment(row.end_time, 'HH:mm:ss').format('h:mm A'),
        'Hours': parseFloat(row.hours).toFixed(2),
        'Description': row.description.replace(/<[^>]*>/g, ''),
      });
    });

    // Add summary at bottom
    allHours.push({});
    allHours.push({
      'Date': 'SUMMARY',
      'Type': '',
      'Client ID': '',
      'Client Name': '',
      'Start Time': '',
      'End Time': '',
      'Hours': '',
      'Description': ''
    });
    allHours.push({
      'Date': 'Employee:',
      'Type': employeeData.employee,
      'Client ID': '',
      'Client Name': '',
      'Start Time': '',
      'End Time': '',
      'Hours': '',
      'Description': ''
    });
    allHours.push({
      'Date': 'Period:',
      'Type': moment(employeeData.startDate).format('MMM D, YYYY'),
      'Client ID': 'to',
      'Client Name': moment(employeeData.endDate).format('MMM D, YYYY'),
      'Start Time': '',
      'End Time': '',
      'Hours': '',
      'Description': ''
    });
    allHours.push({});
    allHours.push({
      'Date': 'Direct (Billable) Hours:',
      'Type': '',
      'Client ID': '',
      'Client Name': '',
      'Start Time': '',
      'End Time': '',
      'Hours': employeeData.summary.totalDirectHours.toFixed(2),
      'Description': ''
    });
    allHours.push({
      'Date': 'Indirect (Non-Billable) Hours:',
      'Type': '',
      'Client ID': '',
      'Client Name': '',
      'Start Time': '',
      'End Time': '',
      'Hours': employeeData.summary.totalIndirectHours.toFixed(2),
      'Description': ''
    });
    allHours.push({
      'Date': 'TOTAL HOURS:',
      'Type': '',
      'Client ID': '',
      'Client Name': '',
      'Start Time': '',
      'End Time': '',
      'Hours': employeeData.summary.totalHours.toFixed(2),
      'Description': ''
    });

    const sheet = XLSX.utils.json_to_sheet(allHours);
    XLSX.utils.book_append_sheet(workbook, sheet, 'Employee Payroll Report');

    XLSX.writeFile(workbook, `Employee_Payroll_${employeeData.employee}_${moment().format('YYYY-MM-DD')}.xlsx`);
    setSnackbarMessage({ message: "Excel file exported successfully", severity: "success" });
  };

  const exportByDateToExcel = () => {
    if (!byDateData) return;

    const workbook = XLSX.utils.book_new();
    
    // Detailed breakdown by client and employee
    const detailedData = [];
    byDateData.clients.forEach(client => {
      client.employees.forEach(employee => {
        employee.directHours.forEach(row => {
          detailedData.push({
            'Client ID': client.clientId,
            'Client Name': client.clientName,
            'Employee': employee.employeeName,
            'Type': 'Direct (Billable)',
            'Date': moment(row.date).format('MMM D, YYYY'),
            'Start Time': moment(row.start_time, 'HH:mm:ss').format('h:mm A'),
            'End Time': moment(row.end_time, 'HH:mm:ss').format('h:mm A'),
            'Hours': parseFloat(row.hours).toFixed(2),
            'Description': row.description.replace(/<[^>]*>/g, '')
          });
        });

        employee.indirectHours.forEach(row => {
          detailedData.push({
            'Client ID': client.clientId,
            'Client Name': client.clientName,
            'Employee': employee.employeeName,
            'Type': 'Indirect (Non-Billable)',
            'Date': moment(row.date).format('MMM D, YYYY'),
            'Start Time': moment(row.start_time, 'HH:mm:ss').format('h:mm A'),
            'End Time': moment(row.end_time, 'HH:mm:ss').format('h:mm A'),
            'Hours': parseFloat(row.hours).toFixed(2),
            'Description': row.description.replace(/<[^>]*>/g, '')
          });
        });
      });

      // Add DO Assets for client
      const doAssets = getClientDOAssets(client.clientId);
      if (doAssets) {
        Object.entries(doAssets.resources).forEach(([type, data]) => {
          detailedData.push({
            'Client ID': client.clientId,
            'Client Name': client.clientName,
            'Employee': '',
            'Type': 'DO Infrastructure',
            'Date': `${type.charAt(0).toUpperCase() + type.slice(1)}s`,
            'Start Time': '',
            'End Time': '',
            'Hours': `${data.count} resources`,
            'Description': `$${data.cost.toFixed(2)}/month`
          });
        });
      }

      detailedData.push({}); // Empty row between clients
    });

    // Add summary at bottom
    detailedData.push({});
    detailedData.push({
      'Client ID': 'SUMMARY',
      'Client Name': '',
      'Employee': '',
      'Type': '',
      'Date': '',
      'Start Time': '',
      'End Time': '',
      'Hours': '',
      'Description': ''
    });
    detailedData.push({
      'Client ID': 'Period:',
      'Client Name': moment(byDateData.startDate).format('MMM D, YYYY'),
      'Employee': 'to',
      'Type': moment(byDateData.endDate).format('MMM D, YYYY'),
      'Date': '',
      'Start Time': '',
      'End Time': '',
      'Hours': '',
      'Description': ''
    });
    detailedData.push({
      'Client ID': 'Total Clients:',
      'Client Name': '',
      'Employee': '',
      'Type': '',
      'Date': '',
      'Start Time': '',
      'End Time': '',
      'Hours': byDateData.summary.clientCount.toString(),
      'Description': ''
    });
    detailedData.push({
      'Client ID': 'Direct (Billable) Hours:',
      'Client Name': '',
      'Employee': '',
      'Type': '',
      'Date': '',
      'Start Time': '',
      'End Time': '',
      'Hours': byDateData.summary.totalDirectHours.toFixed(2),
      'Description': ''
    });
    detailedData.push({
      'Client ID': 'Indirect (Non-Billable) Hours:',
      'Client Name': '',
      'Employee': '',
      'Type': '',
      'Date': '',
      'Start Time': '',
      'End Time': '',
      'Hours': byDateData.summary.totalIndirectHours.toFixed(2),
      'Description': ''
    });
    detailedData.push({
      'Client ID': 'TOTAL HOURS:',
      'Client Name': '',
      'Employee': '',
      'Type': '',
      'Date': '',
      'Start Time': '',
      'End Time': '',
      'Hours': byDateData.summary.totalHours.toFixed(2),
      'Description': ''
    });

    const sheet = XLSX.utils.json_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(workbook, sheet, 'Billing By Date Report');

    XLSX.writeFile(workbook, `Billing_By_Date_${moment(byDateData.startDate).format('YYYY-MM-DD')}_to_${moment(byDateData.endDate).format('YYYY-MM-DD')}.xlsx`);
    setSnackbarMessage({ message: "Excel file exported successfully", severity: "success" });
  };

  const exportMonthlyDOToExcel = () => {
    if (!monthlyDOData) return;

    const workbook = XLSX.utils.book_new();
    
    // Detailed breakdown
    const detailedData = [];
    monthlyDOData.clients.forEach(client => {
      Object.entries(client.resources).forEach(([type, data]) => {
        detailedData.push({
          'Client ID': client.clientId,
          'Client Name': client.clientName,
          'Resource Type': type.charAt(0).toUpperCase() + type.slice(1) + 's',
          'Count': data.count,
          'Monthly Cost': `$${data.cost.toFixed(2)}`
        });
      });
    });

    // Add summary at bottom
    detailedData.push({});
    detailedData.push({
      'Client ID': 'SUMMARY',
      'Client Name': '',
      'Resource Type': '',
      'Count': '',
      'Monthly Cost': ''
    });
    detailedData.push({
      'Client ID': 'Month:',
      'Client Name': moment(monthlyDOData.month, 'YYYY-MM').format('MMMM YYYY'),
      'Resource Type': '',
      'Count': '',
      'Monthly Cost': ''
    });
    detailedData.push({
      'Client ID': 'Total Clients with Assets:',
      'Client Name': '',
      'Resource Type': '',
      'Count': monthlyDOData.clients.length.toString(),
      'Monthly Cost': ''
    });
    detailedData.push({
      'Client ID': 'TOTAL MONTHLY INFRASTRUCTURE COST:',
      'Client Name': '',
      'Resource Type': '',
      'Count': '',
      'Monthly Cost': `$${monthlyDOData.totalCost.toFixed(2)}/month`
    });

    const sheet = XLSX.utils.json_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(workbook, sheet, 'Monthly DO Assets Report');

    XLSX.writeFile(workbook, `Monthly_DO_Assets_${moment(monthlyDOData.month, 'YYYY-MM').format('YYYY-MM')}.xlsx`);
    setSnackbarMessage({ message: "Excel file exported successfully", severity: "success" });
  };

  const selectedClientDetails = clients.find(c => c.ID === selectedClient);

  // Only super_admin can access
  if (user.type !== 'super_admin') {
    return (
      <div className={classes.root}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1">
          You don't have permission to access this page.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.pageTitle}>
          Billing & Reports
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          Professional billing reports for clients and payroll reports for employees
        </Typography>
      </Box>

      <Paper className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => {
            setActiveTab(newValue);
            setClientData(null);
            setEmployeeData(null);
            setByDateData(null);
            setMonthlyDOData(null);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : "off"}
        >
          <Tab label="Client Billing" className={classes.tab} />
          <Tab label="Employee Payroll" className={classes.tab} />
          <Tab label="By Date" className={classes.tab} />
          <Tab label="Monthly DO Assets" className={classes.tab} />
        </Tabs>
      </Paper>

      {/* Controls Card */}
      <Card className={classes.controlsCard}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={activeTab === 2 ? 4 : 3}>
            {activeTab === 0 ? (
              <Autocomplete
                id="billing-client-autocomplete"
                fullWidth
                options={clients}
                value={clients.find(c => c.ID === selectedClient) || null}
                getOptionLabel={(client) => `${client.ID} | ${client.Name}`}
                getOptionSelected={(option, value) => option.ID === value.ID}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Client"
                    variant="outlined"
                    size="small"
                  />
                )}
                onChange={(e, value) => {
                  setSelectedClient(value ? value.ID : "");
                }}
              />
            ) : activeTab === 1 ? (
              <TextField
                select
                fullWidth
                label="Select Employee"
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value="">-- Select Employee --</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.username} value={emp.username}>
                    {emp.first_name} {emp.last_name} ({emp.username})
                  </MenuItem>
                ))}
              </TextField>
            ) : activeTab === 2 ? (
              <Typography variant="h6" style={{ paddingTop: 8 }}>
                All Clients & Employees
              </Typography>
            ) : (
              <TextField
                fullWidth
                type="month"
                label="Select Month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Grid>

          {activeTab !== 3 && (
            <>
              <Grid item xs={12} md={activeTab === 2 ? 3 : 3}>
                <TextField
                  select
                  fullWidth
                  label="Date Range Preset"
                  value={datePreset}
                  onChange={(e) => handlePresetChange(e.target.value)}
                  variant="outlined"
                  size="small"
                >
                  <MenuItem value="custom">Custom Range</MenuItem>
                  {biWeeklyPeriods.map((period) => (
                    <MenuItem key={period.label} value={period.label}>
                      {period.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={activeTab === 3 ? 6 : 2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={activeTab === 0 ? fetchClientBilling : activeTab === 1 ? fetchEmployeePayroll : activeTab === 2 ? fetchByDateReport : fetchMonthlyDOAssets}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <DescriptionIcon />}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Results */}
      {activeTab === 0 && clientData && (
        <Card className={classes.resultsCard}>
          <Box className={classes.reportHeader}>
            <Typography className={classes.reportTitle}>
              Client Billing Report
            </Typography>
            <Typography className={classes.reportSubtitle}>
              {selectedClientDetails?.Name} (ID: {clientData.clientId})
            </Typography>
            <Typography className={classes.reportSubtitle}>
              Period: {moment(clientData.startDate).format('MMM D, YYYY')} - {moment(clientData.endDate).format('MMM D, YYYY')}
            </Typography>

            {/* Digital Ocean Assets */}
            {(() => {
              const doAssets = getClientDOAssets(clientData.clientId);
              if (doAssets) {
                return (
                  <Box className={classes.doAssetsCard}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <CloudIcon style={{ marginRight: 8, color: "#1976d2" }} />
                      <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        Digital Ocean Assets
                      </Typography>
                    </Box>
                    {Object.entries(doAssets.resources).map(([type, data]) => (
                      <Box key={type} className={classes.doAssetRow}>
                        <Typography variant="body2">
                          {type.charAt(0).toUpperCase() + type.slice(1)}s: {data.count}
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          ${data.cost.toFixed(2)}/month
                        </Typography>
                      </Box>
                    ))}
                    <Divider style={{ margin: '8px 0' }} />
                    <Box className={classes.doAssetRow}>
                      <Typography variant="body2" style={{ fontWeight: 700 }}>
                        Total Monthly Cost:
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 700, color: "#1976d2" }}>
                        ${doAssets.totalCost.toFixed(2)}/month
                      </Typography>
                    </Box>
                  </Box>
                );
              }
              return null;
            })()}

            <Box className={classes.actionButtons} mt={2}>
              <Tooltip title="Export to Excel">
                <IconButton size="small" color="primary" onClick={exportClientBillingToExcel}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Direct Hours Section */}
          {clientData.directHours.length > 0 && (
            <>
              <Box className={classes.sectionHeader}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography className={classes.sectionTitle}>
                    Direct (Billable) Hours
                  </Typography>
                  <Chip label="Billable" className={classes.chipDirect} size="small" />
                </Box>
              </Box>
              <TableContainer>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Employee</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell align="center">Hours</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientData.directHours.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{moment(row.date).format('MMM D, YYYY')}</TableCell>
                        <TableCell>{row.employee}</TableCell>
                        <TableCell>
                          {moment(row.start_time, 'HH:mm:ss').format('h:mm A')} - {moment(row.end_time, 'HH:mm:ss').format('h:mm A')}
                        </TableCell>
                        <TableCell align="center">
                          <strong>{parseFloat(row.hours).toFixed(2)}</strong>
                        </TableCell>
                        <TableCell className={classes.descriptionCell}>
                          <div dangerouslySetInnerHTML={{ __html: row.description }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Indirect Hours Section */}
          {clientData.indirectHours.length > 0 && (
            <>
              <Box className={classes.sectionHeader}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography className={classes.sectionTitle}>
                    Indirect (Non-Billable) Hours
                  </Typography>
                  <Chip label="Non-Billable" className={classes.chipIndirect} size="small" />
                </Box>
              </Box>
              <TableContainer>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Employee</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell align="center">Hours</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientData.indirectHours.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{moment(row.date).format('MMM D, YYYY')}</TableCell>
                        <TableCell>{row.employee}</TableCell>
                        <TableCell>
                          {moment(row.start_time, 'HH:mm:ss').format('h:mm A')} - {moment(row.end_time, 'HH:mm:ss').format('h:mm A')}
                        </TableCell>
                        <TableCell align="center">
                          <strong>{parseFloat(row.hours).toFixed(2)}</strong>
                        </TableCell>
                        <TableCell className={classes.descriptionCell}>
                          <div dangerouslySetInnerHTML={{ __html: row.description }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Summary */}
          <Box className={classes.summaryCard}>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 700 }}>
              Summary
            </Typography>
            <Box className={classes.summaryRow}>
              <Typography className={classes.summaryLabel}>
                Direct (Billable) Hours:
              </Typography>
              <Typography className={classes.summaryValue}>
                {clientData.summary.totalDirectHours.toFixed(2)} hrs
              </Typography>
            </Box>
            <Box className={classes.summaryRow}>
              <Typography className={classes.summaryLabel}>
                Indirect (Non-Billable) Hours:
              </Typography>
              <Typography className={classes.summaryValue}>
                {clientData.summary.totalIndirectHours.toFixed(2)} hrs
              </Typography>
            </Box>
            <Box className={`${classes.summaryRow} ${classes.totalRow}`}>
              <Typography className={classes.summaryLabel} style={{ fontSize: "1.1rem" }}>
                Total Hours:
              </Typography>
              <Typography className={classes.summaryValue} style={{ fontSize: "1.3rem" }}>
                {clientData.summary.totalHours.toFixed(2)} hrs
              </Typography>
            </Box>
          </Box>
        </Card>
      )}

      {activeTab === 1 && employeeData && (
        <Card className={classes.resultsCard}>
          <Box className={classes.reportHeader}>
            <Typography className={classes.reportTitle}>
              Employee Payroll Report
            </Typography>
            <Typography className={classes.reportSubtitle}>
              Employee: {selectedEmployee}
            </Typography>
            <Typography className={classes.reportSubtitle}>
              Period: {moment(employeeData.startDate).format('MMM D, YYYY')} - {moment(employeeData.endDate).format('MMM D, YYYY')}
            </Typography>

            <Box className={classes.actionButtons} mt={2}>
              <Tooltip title="Export to Excel">
                <IconButton size="small" color="primary" onClick={exportEmployeePayrollToExcel}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Direct Hours Section */}
          {employeeData.directHours.length > 0 && (
            <>
              <Box className={classes.sectionHeader}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography className={classes.sectionTitle}>
                    Direct (Billable) Hours
                  </Typography>
                  <Chip label="Billable" className={classes.chipDirect} size="small" />
                </Box>
              </Box>
              <TableContainer>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell align="center">Hours</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeData.directHours.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{moment(row.date).format('MMM D, YYYY')}</TableCell>
                        <TableCell>
                          {row.client_id} | {row.client_name || 'Unknown Client'}
                        </TableCell>
                        <TableCell>
                          {moment(row.start_time, 'HH:mm:ss').format('h:mm A')} - {moment(row.end_time, 'HH:mm:ss').format('h:mm A')}
                        </TableCell>
                        <TableCell align="center">
                          <strong>{parseFloat(row.hours).toFixed(2)}</strong>
                        </TableCell>
                        <TableCell className={classes.descriptionCell}>
                          <div dangerouslySetInnerHTML={{ __html: row.description }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Indirect Hours Section */}
          {employeeData.indirectHours.length > 0 && (
            <>
              <Box className={classes.sectionHeader}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography className={classes.sectionTitle}>
                    Indirect (Non-Billable) Hours
                  </Typography>
                  <Chip label="Non-Billable" className={classes.chipIndirect} size="small" />
                </Box>
              </Box>
              <TableContainer>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell align="center">Hours</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeData.indirectHours.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{moment(row.date).format('MMM D, YYYY')}</TableCell>
                        <TableCell>
                          {row.client_id} | {row.client_name || 'Unknown Client'}
                        </TableCell>
                        <TableCell>
                          {moment(row.start_time, 'HH:mm:ss').format('h:mm A')} - {moment(row.end_time, 'HH:mm:ss').format('h:mm A')}
                        </TableCell>
                        <TableCell align="center">
                          <strong>{parseFloat(row.hours).toFixed(2)}</strong>
                        </TableCell>
                        <TableCell className={classes.descriptionCell}>
                          <div dangerouslySetInnerHTML={{ __html: row.description }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Summary */}
          <Box className={classes.summaryCard}>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 700 }}>
              Summary
            </Typography>
            <Box className={classes.summaryRow}>
              <Typography className={classes.summaryLabel}>
                Direct (Billable) Hours:
              </Typography>
              <Typography className={classes.summaryValue}>
                {employeeData.summary.totalDirectHours.toFixed(2)} hrs
              </Typography>
            </Box>
            <Box className={classes.summaryRow}>
              <Typography className={classes.summaryLabel}>
                Indirect (Non-Billable) Hours:
              </Typography>
              <Typography className={classes.summaryValue}>
                {employeeData.summary.totalIndirectHours.toFixed(2)} hrs
              </Typography>
            </Box>
            <Box className={`${classes.summaryRow} ${classes.totalRow}`}>
              <Typography className={classes.summaryLabel} style={{ fontSize: "1.1rem" }}>
                Total Hours:
              </Typography>
              <Typography className={classes.summaryValue} style={{ fontSize: "1.3rem" }}>
                {employeeData.summary.totalHours.toFixed(2)} hrs
              </Typography>
            </Box>
          </Box>
        </Card>
      )}

      {/* By Date Tab - Expandable Client/Employee View */}
      {activeTab === 2 && byDateData && (
        <Card className={classes.resultsCard}>
          <Box className={classes.reportHeader}>
            <Typography className={classes.reportTitle}>
              Billing Report by Date Range
            </Typography>
            <Typography className={classes.reportSubtitle}>
              Period: {moment(byDateData.startDate).format('MMM D, YYYY')} - {moment(byDateData.endDate).format('MMM D, YYYY')}
            </Typography>
            <Typography className={classes.reportSubtitle} style={{ marginTop: 8 }}>
              {byDateData.summary.clientCount} Clients | {byDateData.summary.recordCount} Entries | {byDateData.summary.totalHours.toFixed(2)} Total Hours
            </Typography>

            <Box className={classes.actionButtons} mt={2}>
              <Tooltip title="Export to Excel">
                <IconButton size="small" color="primary" onClick={exportByDateToExcel}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box p={3}>
            {byDateData.clients.map((client) => (
              <Card key={client.clientId} className={classes.clientCard} elevation={2}>
                <Box
                  className={classes.clientCardHeader}
                  onClick={() => toggleClientExpanded(client.clientId)}
                >
                  <Box className={classes.clientSummary}>
                    <Box className={classes.clientInfo}>
                      <Avatar className={classes.clientAvatar}>
                        <BusinessIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                          {client.clientName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          ID: {client.clientId} | {client.employees.length} Employee{client.employees.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className={classes.clientStats}>
                      <Box className={classes.statItem}>
                        <Typography className={classes.statLabel}>Direct</Typography>
                        <Typography className={classes.statValue} style={{ color: "#4caf50" }}>
                          {client.totalDirectHours.toFixed(1)}h
                        </Typography>
                      </Box>
                      <Box className={classes.statItem}>
                        <Typography className={classes.statLabel}>Indirect</Typography>
                        <Typography className={classes.statValue} style={{ color: "#ff9800" }}>
                          {client.totalIndirectHours.toFixed(1)}h
                        </Typography>
                      </Box>
                      <Box className={classes.statItem}>
                        <Typography className={classes.statLabel}>Total</Typography>
                        <Typography className={classes.statValue}>
                          {client.totalHours.toFixed(1)}h
                        </Typography>
                      </Box>
                      <IconButton size="small">
                        {expandedClients[client.clientId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                </Box>

                <Collapse in={expandedClients[client.clientId]} timeout="auto" unmountOnExit>
                  <Box p={2} pt={1}>
                    {client.employees.map((employee) => {
                      const employeeKey = `${client.clientId}-${employee.employeeName}`;
                      return (
                        <Card key={employeeKey} className={classes.employeeCard} elevation={0}>
                          <Box
                            className={classes.employeeHeader}
                            onClick={() => toggleEmployeeExpanded(client.clientId, employee.employeeName)}
                          >
                            <Box className={classes.employeeInfo}>
                              <Avatar className={classes.employeeAvatar}>
                                <PersonIcon />
                              </Avatar>
                              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                {employee.employeeName}
                              </Typography>
                            </Box>
                            <Box className={classes.employeeStats}>
                              <Chip 
                                label={`Direct: ${employee.totalDirectHours.toFixed(1)}h`} 
                                size="small" 
                                className={classes.chipDirect}
                              />
                              <Chip 
                                label={`Indirect: ${employee.totalIndirectHours.toFixed(1)}h`} 
                                size="small" 
                                className={classes.chipIndirect}
                              />
                              <Typography className={classes.miniStat}>
                                Total: {employee.totalHours.toFixed(1)}h
                              </Typography>
                              <IconButton size="small">
                                {expandedEmployees[employeeKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Box>
                          </Box>

                          <Collapse in={expandedEmployees[employeeKey]} timeout="auto" unmountOnExit>
                            <Box p={2} pt={0}>
                              {/* Direct Hours */}
                              {employee.directHours.length > 0 && (
                                <>
                                  <Box display="flex" alignItems="center" mt={2} mb={1}>
                                    <Chip label="Direct (Billable)" className={classes.chipDirect} size="small" />
                                    <Typography variant="caption" style={{ marginLeft: 8, color: "#666" }}>
                                      {employee.directHours.length} entries
                                    </Typography>
                                  </Box>
                                  <TableContainer>
                                    <Table size="small" className={classes.table}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Date</TableCell>
                                          <TableCell>Time</TableCell>
                                          <TableCell align="center">Hours</TableCell>
                                          <TableCell>Description</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {employee.directHours.map((row, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{moment(row.date).format('MMM D')}</TableCell>
                                            <TableCell>
                                              {moment(row.start_time, 'HH:mm:ss').format('h:mm A')} - {moment(row.end_time, 'HH:mm:ss').format('h:mm A')}
                                            </TableCell>
                                            <TableCell align="center">
                                              <strong>{parseFloat(row.hours).toFixed(2)}</strong>
                                            </TableCell>
                                            <TableCell className={classes.descriptionCell}>
                                              <div dangerouslySetInnerHTML={{ __html: row.description }} />
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              )}

                              {/* Indirect Hours */}
                              {employee.indirectHours.length > 0 && (
                                <>
                                  <Box display="flex" alignItems="center" mt={2} mb={1}>
                                    <Chip label="Indirect (Non-Billable)" className={classes.chipIndirect} size="small" />
                                    <Typography variant="caption" style={{ marginLeft: 8, color: "#666" }}>
                                      {employee.indirectHours.length} entries
                                    </Typography>
                                  </Box>
                                  <TableContainer>
                                    <Table size="small" className={classes.table}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Date</TableCell>
                                          <TableCell>Time</TableCell>
                                          <TableCell align="center">Hours</TableCell>
                                          <TableCell>Description</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {employee.indirectHours.map((row, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{moment(row.date).format('MMM D')}</TableCell>
                                            <TableCell>
                                              {moment(row.start_time, 'HH:mm:ss').format('h:mm A')} - {moment(row.end_time, 'HH:mm:ss').format('h:mm A')}
                                            </TableCell>
                                            <TableCell align="center">
                                              <strong>{parseFloat(row.hours).toFixed(2)}</strong>
                                            </TableCell>
                                            <TableCell className={classes.descriptionCell}>
                                              <div dangerouslySetInnerHTML={{ __html: row.description }} />
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              )}
                            </Box>
                          </Collapse>
                        </Card>
                      );
                    })}

                    {/* Digital Ocean Assets Section */}
                    {(() => {
                      const doAssets = getClientDOAssets(client.clientId);
                      if (doAssets) {
                        return (
                          <Box className={classes.doAssetsCard} mt={2}>
                            <Box display="flex" alignItems="center" mb={1}>
                              <CloudIcon style={{ marginRight: 8, color: "#1976d2" }} />
                              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                Digital Ocean Infrastructure
                              </Typography>
                            </Box>
                            <Divider style={{ margin: '8px 0' }} />
                            {Object.entries(doAssets.resources).map(([type, data]) => (
                              <Box key={type} className={classes.doAssetRow}>
                                <Typography variant="body2">
                                  {type.charAt(0).toUpperCase() + type.slice(1)}s: {data.count}
                                </Typography>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                  ${data.cost.toFixed(2)}/month
                                </Typography>
                              </Box>
                            ))}
                            <Divider style={{ margin: '8px 0' }} />
                            <Box className={classes.doAssetRow}>
                              <Typography variant="body2" style={{ fontWeight: 700 }}>
                                Total Monthly Infrastructure Cost:
                              </Typography>
                              <Typography variant="body2" style={{ fontWeight: 700, color: "#1976d2" }}>
                                ${doAssets.totalCost.toFixed(2)}/month
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                </Collapse>
              </Card>
            ))}

            {/* Grand Total Summary */}
            <Box className={classes.summaryCard} mt={3}>
              <Typography variant="h6" gutterBottom style={{ fontWeight: 700 }}>
                Grand Total Summary
              </Typography>
              <Box className={classes.summaryRow}>
                <Typography className={classes.summaryLabel}>
                  Total Clients:
                </Typography>
                <Typography className={classes.summaryValue}>
                  {byDateData.summary.clientCount}
                </Typography>
              </Box>
              <Box className={classes.summaryRow}>
                <Typography className={classes.summaryLabel}>
                  Direct (Billable) Hours:
                </Typography>
                <Typography className={classes.summaryValue}>
                  {byDateData.summary.totalDirectHours.toFixed(2)} hrs
                </Typography>
              </Box>
              <Box className={classes.summaryRow}>
                <Typography className={classes.summaryLabel}>
                  Indirect (Non-Billable) Hours:
                </Typography>
                <Typography className={classes.summaryValue}>
                  {byDateData.summary.totalIndirectHours.toFixed(2)} hrs
                </Typography>
              </Box>
              <Box className={`${classes.summaryRow} ${classes.totalRow}`}>
                <Typography className={classes.summaryLabel} style={{ fontSize: "1.1rem" }}>
                  Total Hours:
                </Typography>
                <Typography className={classes.summaryValue} style={{ fontSize: "1.3rem" }}>
                  {byDateData.summary.totalHours.toFixed(2)} hrs
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      )}

      {/* Monthly DO Assets Tab */}
      {activeTab === 3 && monthlyDOData && (
        <Card className={classes.resultsCard}>
          <Box className={classes.reportHeader}>
            <Typography className={classes.reportTitle}>
              Monthly Digital Ocean Assets Report
            </Typography>
            <Typography className={classes.reportSubtitle}>
              Month: {moment(monthlyDOData.month, 'YYYY-MM').format('MMMM YYYY')}
            </Typography>
            <Typography className={classes.reportSubtitle} style={{ marginTop: 8 }}>
              {monthlyDOData.clients.length} Clients | Total Infrastructure Cost: ${monthlyDOData.totalCost.toFixed(2)}/month
            </Typography>

            <Box className={classes.actionButtons} mt={2}>
              <Tooltip title="Export to Excel">
                <IconButton size="small" color="primary" onClick={exportMonthlyDOToExcel}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box p={3}>
            {monthlyDOData.clients.map((client) => (
              <Card key={client.clientId} className={classes.clientCard} elevation={2}>
                <Box
                  className={classes.clientCardHeader}
                  onClick={() => toggleDOClientExpanded(client.clientId)}
                >
                  <Box className={classes.clientSummary}>
                    <Box className={classes.clientInfo}>
                      <Avatar className={classes.clientAvatar} style={{ backgroundColor: "#1976d2" }}>
                        <CloudIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                          {client.clientName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          ID: {client.clientId} | {Object.keys(client.resources).length} Resource Type(s)
                        </Typography>
                      </Box>
                    </Box>
                    <Box className={classes.clientStats}>
                      <Box className={classes.statItem}>
                        <Typography className={classes.statLabel}>Monthly Cost</Typography>
                        <Typography className={classes.statValue} style={{ fontSize: "1.3rem" }}>
                          ${client.totalCost.toFixed(2)}
                        </Typography>
                      </Box>
                      <IconButton size="small">
                        {expandedDOClients[client.clientId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                </Box>

                <Collapse in={expandedDOClients[client.clientId]} timeout="auto" unmountOnExit>
                  <Box p={3}>
                    <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 16 }}>
                      Asset Breakdown
                    </Typography>
                    <TableContainer>
                      <Table className={classes.table}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Resource Type</TableCell>
                            <TableCell align="center">Count</TableCell>
                            <TableCell align="right">Monthly Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(client.resources).map(([type, data]) => (
                            <TableRow key={type}>
                              <TableCell>
                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)}s
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={data.count} color="primary" size="small" />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body1" style={{ fontWeight: 600, color: "#1976d2" }}>
                                  ${data.cost.toFixed(2)}/month
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell colSpan={2}>
                              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                Total Infrastructure Cost
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" style={{ fontWeight: 700, color: "#1976d2" }}>
                                ${client.totalCost.toFixed(2)}/month
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Collapse>
              </Card>
            ))}

            {/* Grand Total */}
            <Box className={classes.summaryCard} mt={3}>
              <Typography variant="h6" gutterBottom style={{ fontWeight: 700 }}>
                Total Summary
              </Typography>
              <Box className={classes.summaryRow}>
                <Typography className={classes.summaryLabel}>
                  Total Clients with Assets:
                </Typography>
                <Typography className={classes.summaryValue}>
                  {monthlyDOData.clients.length}
                </Typography>
              </Box>
              <Box className={`${classes.summaryRow} ${classes.totalRow}`}>
                <Typography className={classes.summaryLabel} style={{ fontSize: "1.1rem" }}>
                  Total Monthly Infrastructure Cost:
                </Typography>
                <Typography className={classes.summaryValue} style={{ fontSize: "1.3rem" }}>
                  ${monthlyDOData.totalCost.toFixed(2)}/month
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !clientData && !employeeData && !byDateData && !monthlyDOData && (
        <Box className={classes.emptyState}>
          <DescriptionIcon style={{ fontSize: 64, color: "#ccc", marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom>
            No Report Generated
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {activeTab === 0 && "Select a client and date range, then click 'Generate Report'"}
            {activeTab === 1 && "Select an employee and date range, then click 'Generate Report'"}
            {activeTab === 2 && "Select a date range, then click 'Generate Report' to see all work"}
            {activeTab === 3 && "Select a month, then click 'Generate Report' to see all Digital Ocean assets"}
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default BillingReports;
